import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { WorkItemTypeIcon } from './WorkItemTypeIcon'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useWorkItemTypesForProject } from '@/lib/db/rxdb/hooks/entities/work-item-types'
import { resolveDefaultWorkItemTypeId } from '@/lib/types/workItemType'

interface WorkItemTypePickerProps {
  currentTypeId?: string | null
  onTypeChange: (typeId: string) => void
  /** When set, only show types available to this project. */
  projectId?: string | null
  buttonClassName?: string
}

export function WorkItemTypePicker({
  currentTypeId,
  onTypeChange,
  projectId,
  buttonClassName,
}: WorkItemTypePickerProps) {
  const { t } = useTranslation('tasks')
  const [open, setOpen] = React.useState(false)
  const { workItemTypes } = useWorkItemTypesForProject(projectId)

  const effectiveTypeId =
    currentTypeId ?? resolveDefaultWorkItemTypeId(workItemTypes)

  const currentType = React.useMemo(
    () => workItemTypes.find((t) => t.id === effectiveTypeId),
    [workItemTypes, effectiveTypeId],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('justify-start', buttonClassName)}
        >
          {currentType ? (
            <div className="flex items-center gap-2">
              <WorkItemTypeIcon icon={currentType.icon} color={currentType.color} />
              <span>{currentType.name}</span>
            </div>
          ) : (
            <span className="text-muted-foreground">{t('workItemType.setType')}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-2" align="start">
        <div className="space-y-0.5">
          {workItemTypes.map((type) => {
            const isSelected = type.id === effectiveTypeId
            return (
              <Button
                key={type.id}
                variant="ghost"
                size="sm"
                className={cn('w-full justify-start', isSelected && 'bg-accent')}
                onClick={() => {
                  onTypeChange(type.id)
                  setOpen(false)
                }}
              >
                <div className="flex items-center gap-2 flex-1">
                  <WorkItemTypeIcon icon={type.icon} color={type.color} />
                  <span>{type.name}</span>
                </div>
                {isSelected && <Check className="size-4 ml-auto shrink-0" />}
              </Button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
